import { create } from 'zustand';

import { api, setAccessToken, setSelectedTenantId } from './api';

export interface CurrentUser {
  id: string;
  principal_id: string;
  tenant_id: string | null;
  email: string;
  display_name: string | null;
  avatar_url?: string | null;
  status: string;
  is_platform_admin: boolean;
  effective_permissions: string[];
}

export interface ImpersonationState {
  active: boolean;
  real_user_id: string | null;
}

export interface SelectedTenant {
  tenant_id: string;
  name: string;
  slug: string | null;
  status: string;
  is_platform_admin: boolean;
}

export interface TenantSummary extends SelectedTenant {}

interface AuthState {
  user: CurrentUser | null;
  impersonation: ImpersonationState;
  tenants: TenantSummary[];
  selectedTenant: SelectedTenant | null;
  hydrated: boolean;
  setSession: (user: CurrentUser, accessToken: string, impersonation: ImpersonationState) => void;
  setUser: (user: CurrentUser, impersonation: ImpersonationState) => void;
  setTenants: (tenants: TenantSummary[]) => void;
  setSelectedTenant: (snapshot: SelectedTenant | null) => void;
  clearSession: () => void;
  markHydrated: () => void;
}

const INITIAL_IMP: ImpersonationState = { active: false, real_user_id: null };
const TENANT_STORAGE_KEY = 'saas-kit.selected-tenant-by-user';

function readStoredTenantMap(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(TENANT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeStoredTenantMap(value: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignore storage failures */
  }
}

function persistSelectedTenant(userId: string | undefined, tenantId: string | null): void {
  if (!userId) return;
  const next = readStoredTenantMap();
  if (tenantId) next[userId] = tenantId;
  else delete next[userId];
  writeStoredTenantMap(next);
}

export function resolveStoredSelectedTenant(
  userId: string,
  tenants: TenantSummary[],
  lastActiveTenantId?: string | null,
): SelectedTenant | null {
  if (tenants.length === 0) return null;
  if (tenants.length === 1) return tenants[0] ?? null;
  // Prefer the server-remembered "last active" tenant — it survives a new
  // device or browser, unlike the localStorage fallback below.
  if (lastActiveTenantId) {
    const remembered = tenants.find((t) => t.tenant_id === lastActiveTenantId);
    if (remembered) return remembered;
  }
  const storedTenantId = readStoredTenantMap()[userId];
  if (storedTenantId) {
    const stored = tenants.find((t) => t.tenant_id === storedTenantId);
    if (stored) return stored;
  }
  // Never block a multi-tenant user with a full-page chooser: default to the
  // first workspace. They switch workspaces from the top-nav switcher.
  return tenants[0] ?? null;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  impersonation: INITIAL_IMP,
  tenants: [],
  selectedTenant: null,
  hydrated: false,
  setSession: (user, token, impersonation) => {
    setAccessToken(token);
    set({ user, impersonation, hydrated: true });
  },
  setUser: (user, impersonation) => {
    set({ user, impersonation, hydrated: true });
  },
  setTenants: (tenants) => set({ tenants }),
  setSelectedTenant: (snapshot) =>
    set((state) => {
      setSelectedTenantId(snapshot?.tenant_id ?? null);
      persistSelectedTenant(state.user?.id, snapshot?.tenant_id ?? null);
      return { selectedTenant: snapshot };
    }),
  clearSession: () => {
    setAccessToken(null);
    setSelectedTenantId(null);
    set({
      user: null,
      impersonation: INITIAL_IMP,
      tenants: [],
      selectedTenant: null,
      hydrated: true,
    });
  },
  markHydrated: () => set({ hydrated: true }),
}));

export async function logout(): Promise<void> {
  await api.post('/auth/logout').catch(() => undefined);
}
